// System directives

"use client";

// System dependencies

import { useState, useEffect, useRef } from "react";

// Application dependencies

import {
  AUTH_TOTP_COOKIE,
  AUTH_TOTP_DEFAULT_SECRET,
  AuthChallenge,
  AuthChallengeStatus,
  AuthCookieData,
  AuthFields,
  AuthTotpError,
  AuthTotpStatus,
} from "@/_auth/interfaces/auth.interfaces";
import { decryptCookie, encryptCookie, generateSignature } from "@/_auth/lib/auth.functions";
import { devConsole } from "@/lib/devConsole";
import { formatErrorMessage, generateUuid } from "@/lib/functions";
import { getAuthFactorIdAction } from "@/_auth/actions/auth.actions";
import { supabaseClient } from "@/_auth/utils/supabase/client";

// Namespace

const NAMESPACE = "useMfsCookies";

//devConsole.log(NAMESPACE, ``);

const useMfaCookie = () => {
  // Application state.

  const [cookieData, setCookieData] = useState<AuthCookieData | null>(null);
  const effectRan = useRef(false);
  const isProduction = process.env.NODE_ENV === "production";
  const secretKey = process.env.NEXT_PUBLIC_COOKIE_SECRET || AUTH_TOTP_DEFAULT_SECRET;

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Use effect to handle the component mount
  // ---------------------------------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    // Check to make sure the useEffect has not already run. This is to protect action strict mode in development.

    if (effectRan.current) return;

    // Set the effectRan ref.

    effectRan.current = true;

    // Thie method handles the setting of the cookie data.

    const fetchCookieData = async () => {
      // Try and read the cookie.

      const data = await readCookie();

      // Store the cookie in state.

      setCookieData(data);
    };

    // Make the call to fetch the cookie data.

    fetchCookieData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Hook Methods
  // ---------------------------------------------------------------------------------------------------------------------------------------

  /**
   * Validates the MFA cookie by ensuring it belongs to the current user, matches the current session, and has not expired or been
   * tampered with.
   *
   * @async
   * @function checkCookie
   * @returns {Promise<boolean>} A boolean indicating whether the cookie is valid.
   * @throws {Error} Logs detailed warnings or errors if the cookie is invalid or validation fails.
   *
   */

  const checkCookie = async (): Promise<boolean> => {
    try {
      // Fetch the current user locally.

      const {
        data: { user },
        error,
      } = await supabaseClient.auth.getUser();

      // See whether there is an error or there is no user and delete the cookie and return false.

      if (error || !user) {
        devConsole.warn(NAMESPACE, `checkCookie`, `Error fetching user: ${formatErrorMessage(error) || "No user found"}`);
        await deleteCookie();
        return false;
      }

      // Read and decrypt the cookie.

      const cookie = await readCookie();

      // Check we have a cookie, if not, return false.

      if (!cookie) {
        devConsole.warn(NAMESPACE, "checkCookie", `Error fetching cookie: No cookie found`);
        return false;
      }

      // Validate the user ID and delete the cookie and return false if it doesn't match.

      if (cookie[AuthFields.UserId] !== user.id) {
        devConsole.warn(NAMESPACE, "checkCookie", `Error fetching cookie: User id does not match`);
        await deleteCookie();
        return false;
      }

      // Validate the signature.

      const validSignature = generateSignature(
        cookie[AuthFields.ExpiresAt],
        cookie[AuthFields.IssuedAt],
        cookie[AuthFields.MfaStatus],
        process.env.NEXT_PUBLIC_COOKIE_SECRET || AUTH_TOTP_DEFAULT_SECRET,
        cookie[AuthFields.SessionId],
        cookie[AuthFields.UserId],
      );

      // Check that the cookie signature and delete the cookie and return false if it doesn't match.

      if (cookie[AuthFields.Signature] !== validSignature) {
        devConsole.warn(NAMESPACE, "checkCookie", "Signature mismatch. Cookie is invalid.");
        await deleteCookie();
        return false;
      }

      // Validate the expiration date. This is left out as modern browsers handle cookie expiry with the (expires/max-age) value. If we
      // ever experience an issue with legacy browsers we could introduce this code.

      // if (new Date(cookie[AuthFields.expires_at]) < new Date()) {
      //   devConsole.warn(NAMESPACE, "checkCookie", "Cookie has expired.");
      //   await deleteCookie();
      //   return false;
      // }

      // All checks passed so we can return true.

      return true;
    } catch (error) {
      // This catched any untrapped errors. We can delete the cookie and return false.

      console.warn("useMfsCookie", `checkCookie`, `Cookie validation failed: ${formatErrorMessage(error) || "Unexpected error"}`);
      await deleteCookie();
      return false;
    }
  };

  /**
   * Creates an encrypted MFA cookie with user and session information.
   *
   * @async
   * @returns {Promise<AuthTotpError>} An object indicating the success or failure of cookie creation.
   */

  const createCookie = async (): Promise<AuthTotpError> => {
    // Try and create the cookie.

    try {
      // Fetch the factor ID from the server action.

      const factorId = await getAuthFactorIdAction();

      // Fetch user data (it's needed for userId).

      const {
        data: { user },
      } = await supabaseClient.auth.getUser();

      // Make sure we have a factor and a user. If not return an error.

      if (!factorId || !user) {
        devConsole.log(NAMESPACE, `createCookie`, `Factor or user data is missing. Cannot create TOTP cookie.`);
        return { code: "400", message: "Factor or user data is missing. Cannot create TOTP cookie." };
      }

      // Set the cookie credentials and flags.

      const expiresAt = new Date(Date.now() + 10 * 60 * 60 * 1000).toISOString();
      const issuedAt = new Date().toISOString();
      const sessionId = generateUuid();
      const signature = generateSignature(expiresAt, issuedAt, AuthTotpStatus.Verified, secretKey, sessionId, user.id);

      // Ensure NEXT_PUBLIC_COOKIE_SECRET is secure. This makes sure that our NEXT_PUBLIC_COOKIE_SECRET is being read correctly and reports
      // (but does not halt) the problem to the console.

      if (secretKey === AUTH_TOTP_DEFAULT_SECRET) {
        devConsole.warn(
          NAMESPACE,
          `createCookie`,
          `Using default-secret for NEXT_PUBLIC_COOKIE_SECRET. Replace with a secure key in production.`,
        );
      }

      // Encrypt the cookie body. Encrypting the cookie body ensures sensitive data is protected and tamper-proof while stored in the
      // browser.

      const cookieValue = encryptCookie(
        {
          [AuthFields.ExpiresAt]: expiresAt,
          [AuthFields.IssuedAt]: issuedAt,
          [AuthFields.MfaStatus]: AuthTotpStatus.Verified,
          [AuthFields.SessionId]: sessionId,
          [AuthFields.Signature]: signature,
          [AuthFields.UserId]: user.id,
        },
        secretKey,
      );

      // Create the cookie.

      document.cookie = `${AUTH_TOTP_COOKIE}=${encodeURIComponent(cookieValue)}; Path=/; Expires=${new Date(expiresAt).toUTCString()}; SameSite=Strict; ${isProduction ? "Secure" : ""}`;

      // And store the cookie details in state.

      setCookieData({
        [AuthFields.ExpiresAt]: expiresAt,
        [AuthFields.IssuedAt]: issuedAt,
        [AuthFields.MfaStatus]: AuthTotpStatus.Verified,
        [AuthFields.SessionId]: sessionId,
        [AuthFields.Signature]: signature,
        [AuthFields.UserId]: user.id,
      });

      // Return a success response.

      devConsole.log(NAMESPACE, `createCookie`, `TOTP cookie created successfully.`);

      return { code: "200", message: "TOTP cookie created successfully." };
    } catch (error) {
      // Return an error response.

      devConsole.info(NAMESPACE, `createCookie`, `Error creating cookie: ${formatErrorMessage(error)}`);
      return { code: "400", message: `Error creating cookie: ${formatErrorMessage(error)}` };
    }
  };

  /**
   * Deletes the MFA cookie from the client.
   *
   * @async
   * @returns {Promise<void>} Resolves when the cookie is deleted.
   */

  const deleteCookie = async (): Promise<AuthTotpError> => {
    try {
      // Use document.cookie to explicitly delete the cookie on the client side. Setting the expiration date to the past is the standard way
      // to remove a cookie.

      document.cookie = `${AUTH_TOTP_COOKIE}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict; ${isProduction ? "Secure" : ""}`;

      // Clear the cookie state.

      setCookieData(null);

      // Return a success response.

      console.log("useMfaCookie", `deleteCookie`, `TOTP cookie deleted successfully.`);

      return { code: "200", message: "TOTP cookie deleted successfully." };
    } catch (error) {
      // Return an error response.

      console.info("useMfaCookie", `deleteCookie`, `Error deleting cookie: ${formatErrorMessage(error)}`);
      return { code: "400", message: `Error deleting cookie: ${formatErrorMessage(error)}` };
    }
  };

  /**
   * Initiates a TOTP challenge for the user.
   *
   * @returns {Promise<AuthChallenge>} An object containing the challenge ID, error, and status.
   */

  const initiateChallenge = async (): Promise<AuthChallenge> => {
    try {
      // Fetch the factor ID from the server action

      const factorId = await getAuthFactorIdAction();

      // If no factor ID is found, return failure

      if (!factorId) {
        devConsole.error(NAMESPACE, `initiateChallenge`, `No TOTP factor found for the user.`);
        return {
          [AuthFields.Id]: null,
          [AuthFields.Error]: "No TOTP factor found for the user.",
          [AuthFields.Status]: AuthChallengeStatus.Failure,
        };
      }

      // Initiate the TOTP challenge via Supabase

      const { data, error } = await supabaseClient.auth.mfa.challenge({ factorId });

      // Handle API error

      if (error) {
        devConsole.error(NAMESPACE, `initiateChallenge`, `Error initiating TOTP challenge: ${formatErrorMessage(error)}`);
        return {
          [AuthFields.Id]: null,
          [AuthFields.Error]: formatErrorMessage(error),
          [AuthFields.Status]: AuthChallengeStatus.Failure,
        };
      }

      // Handle missing challenge ID

      if (!data?.[AuthFields.Id]) {
        devConsole.error(NAMESPACE, `initiateChallenge`, `No TOTP challenge ID returned.`);
        return {
          [AuthFields.Id]: null,
          [AuthFields.Error]: "No TOTP challenge ID returned.",
          [AuthFields.Status]: AuthChallengeStatus.Failure,
        };
      }

      // Return success with the challenge ID

      return {
        [AuthFields.Id]: data[AuthFields.Id],
        [AuthFields.Error]: null,
        [AuthFields.Status]: AuthChallengeStatus.Success,
      };
    } catch (error) {
      // Handle unexpected errors

      console.error("useMfaCookie", `initiateChallenge`, `Unexpected error: ${formatErrorMessage(error)}`);
      return {
        [AuthFields.Id]: null,
        [AuthFields.Error]: `Unexpected error: ${formatErrorMessage(error)}`,
        [AuthFields.Status]: AuthChallengeStatus.Failure,
      };
    }
  };

  /**
   * Reads and decrypts the MFA cookie to retrieve session and user information.
   *
   * @async
   * @returns {Promise<AuthCookieData | null>} Decrypted cookie data or null if the cookie is not found.
   */

  const readCookie = async (): Promise<AuthCookieData | null> => {
    try {
      // Try and gget the cookie.

      const cookie = document.cookie
        .split("; ")
        .find((row) => row.startsWith(`${AUTH_TOTP_COOKIE}=`))
        ?.split("=")[1];

      // If there is no cookie we can return null.

      if (!cookie) return null;

      // If there is a cookie we can return the decoded content.

      return decryptCookie(decodeURIComponent(cookie), secretKey);
    } catch (error) {
      // To be here that has been an error so we can return null.

      devConsole.info(NAMESPACE, `readCookie`, `Error reading TOTP cookie: ${formatErrorMessage(error)}`);
      return null;
    }
  };

  return { checkCookie, cookieData, createCookie, deleteCookie, initiateChallenge, readCookie };
};

export default useMfaCookie;
